import { property } from "lit/decorators.js";
import { css, html, unsafeCSS } from 'lit';
import { SectionPageBase } from "./base/section-page.base";
import type { GetSection } from "../../../core/parsers/commands/get-section";
import type { PropertyValues } from "lit";

import tailwindCSS from '../../../index.css?inline';
import { TemplateMixin } from "../../../core/mixins/template-mixin";
import { callServiceParams } from "../../../core/mixins/http-mixin-2";


export class SectionPage extends TemplateMixin(SectionPageBase) {  
    @property({ type: Object })
    params: Record<string, any> = {};

    @property({ type: Object })
    payload: GetSection | any;

    @property({ type: String })
    service: string = '';

    @property({ type: Boolean })
    loading = false;

    @property({ type: Object })
    pageGroup: any = null;

    @property({ type: Boolean })
    closeEnabled = true; 

    @property({ type: Function })
    closeDialog?: () => void; // Add closeDialog function property

    static styles = [
        unsafeCSS(tailwindCSS),
        css`

                /* Fix scrollbar for sidebar */
            .sidebar-scroll {
            scrollbar-width: thin;
            scrollbar-color: var(--md-sys-color-primary) transparent;
            }

            .sidebar-scroll::-webkit-scrollbar {
            width: 4px;
            }

            .sidebar-scroll::-webkit-scrollbar-track {
            background: transparent;
            }

            .sidebar-scroll::-webkit-scrollbar-thumb {
            background-color: var(--md-sys-color-outlineVariant);
            border-radius: 2px;
            }

        `
    ]

    constructor() {
        super();
        this.params = {};
    }

    protected async firstUpdated(changedProperties: PropertyValues): Promise<void> {
        super.firstUpdated(changedProperties);
                
        // If service is provided, load it automatically
        if (this.service && !this.payload) {
            try {
                this.loading = true;
                this.requestUpdate();
                console.log("about to call service ",this.service,this.params)
                const response = await callServiceParams(this.service, this.params);
                this.handleResponse(response.response);
            } catch (error) {
                console.error('Error loading service:', error);
                this.handleServiceError(error);
            }
        }
        
        // Fallback: get params from my-element if not provided
        if (Object.keys(this.params).length === 0) {
            const au = document.querySelector('my-element');
            const p = au ? au.getAttribute('params') : null;
            this.params = p ? JSON.parse(p) : {};
        }
    }

    // This method will be called by TemplateMixin
    async loadServiceParams(service: string, params: Record<string, any>): Promise<void> {
        this.service = service;
        this.params = { ...this.params, ...params };
        this.loading = true;
        this.requestUpdate();
        console.log("service in loadservice params is ",this.service)
        
        try {
            const response = await this.callServiceParams(this.service, this.params);
            console.log("response in loadServiceParams is ",response)
            this.handleResponse(response.response);
        } catch (error) {
            console.error('Error in loadServiceParams:', error);
            this.handleServiceError(error);
        }
    }


    private _handleClose() {
        // If a closeDialog function is provided, call it
        if (this.closeDialog && typeof this.closeDialog === 'function') {
            this.closeDialog();
        } else {
            // Fallback: dispatch a close event
            this.dispatchEvent(new CustomEvent('close-dialog', {
                bubbles: true,
                composed: true
            }));
            
            // Or fallback to parent method if available
            if (window.parent && (window.parent as any).closeDialog) {
                (window.parent as any).closeDialog();
            }
        }
    }

    render() {
        
        // Show loader while loading or until pageGroup is available
        if (this.loading || !this.pageGroup) {
            return html`
                <div class="flex items-center justify-center p-8 min-h-[200px]">
                    <div class="text-center">
                        <div class="loader mb-4"></div>
                    </div>
                </div>
                
                <style>
                    .loader {
                        width: 48px;
                        height: 48px;
                        border: 3px solid #f3f3f3;
                        border-top: 3px solid #3498db;
                        border-radius: 50%;
                        animation: spin 1s linear infinite;
                        display: inline-block;
                    }
                    
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                </style>
            `;
        }

        // Render the page group with form-render components
        return html`
            <div class="w-full">
                <div>
                    ${this.pageGroup.pages && this.pageGroup.pages.length > 0 ? 
                        this.pageGroup.pages.map((page: any) => html`        
                            <section class="w-full mb-6 border-b last:border-b-0" id=${page.id}>
                                ${page.title ? html`
                                    <div class="flex items-center justify-between bg-[var(--md-sys-color-primary)] text-[var(--md-sys-color-onPrimary)] p-4">
                                        <h2 class="text-lg font-semibold">${page.title}</h2>
                                        ${this.closeEnabled ? html`
                                            <button 
                                                class="flex items-center justify-center w-8 h-8 rounded-full hover:bg-[var(--md-sys-color-onPrimary)] hover:bg-opacity-10 transition-colors group"
                                                @click=${this._handleClose}
                                                aria-label="Close page"
                                            >
                                                <svg class="w-5 h-5 fill-[var(--md-sys-color-onPrimary)] group-hover:fill-[var(--md-sys-color-primary)] transition-colors" viewBox="0 0 24 24">
                                                    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                                                </svg>
                                            </button>
                                        ` : ''}
                                    </div>
                                ` : ''}
                                <div class="w-full flex flex-wrap">
                                    ${page._feedList && page._feedList.length > 0 ? 
                                        this.renderFeeds(page._feedList) : 
                                        html`<div class="w-full p-4 text-gray-500 text-center">No feeds available</div>`
                                    }
                                </div>
                            </section>
                        `) : 
                        html`<div class="w-full p-8 text-gray-500 text-center">No pages available in this section</div>`
                    }
                </div>
            </div>
        `;
    }

    renderFeeds(feeds: any[]) {
        return feeds.map((feed: any, index: number) => {
            const isLastItem = index === feeds.length - 1;
            const isOddCount = feeds.length % 2 !== 0;
            
            // 如果 feeds 数量是奇数且这是最后一个 feed，则使用 w-full
            let gridClasses = this._gridClasses(feed);
            if (isLastItem && isOddCount) {
                gridClasses = 'w-full';
            }
            
            return html`
                <div class="${gridClasses} p-2 sidebar-scroll" style="max-height: 77vh;overflow: scroll;">
                    <form-render
                        parent="dialog" 
                        .template=${window.TEMPLATE}
                        .feed="${feed}" 
                        .top="${this._computeTop()}" 
                        .params="${this.params}"
                        class="w-full"
                    ></form-render>
                </div>
            `;
        });
    }
}

customElements.define('section-page', SectionPage as any);
